/**
 * V5.1 — Feature-Builder.
 *
 * Mappt vorhandene V5-Datenstrukturen (Quote, OhlcvFeatures, AIResearch,
 * Earnings, WatchlistEntry) auf das V5.1-`StrategyFeatures`-Objekt.
 *
 * Nicht-invasiv: nutzt die bereits in der App geladenen Daten, kein neuer
 * Provider-Call.
 */

import type { PriceQuote } from '@/lib/market-data/types';
import type { OhlcvFeatures } from '@/lib/scoring/ohlcv-features';
import type { AIResearchData } from '@/lib/ai-research';
import type { AssetClass, AssetSubtype, ThemeTag, WatchlistEntry } from '@/types';
import type { StrategyFeatures } from './types';
import { inferAssetSubtype } from './asset-subtype';

interface BuildFeaturesInput {
  entry: WatchlistEntry;
  quote: PriceQuote | null;
  ohlcv_daily: OhlcvFeatures | null;
  ohlcv_weekly: OhlcvFeatures | null;
  ai: AIResearchData | null;
  days_to_earnings: number | null;
  last_earnings_surprise_pct?: number | null;
  last_earnings_day_reaction_pct?: number | null;
  /** BTC-Dominanz (Krypto-Kontext, optional). */
  btc_dominance_pct?: number | null;
  /** W78.5 — FINRA Short-Volume-Pct. */
  short_interest_pct_of_float?: number | null;
  /** W78.6 — Letzte 8 Earnings-Surprises in % (juengstes zuerst). */
  historical_surprises_pct?: number[] | null;
  /**
   * W73 Phase 6 — Drawdown vom ATH (Krypto-spezifisch). Aus
   * v5_crypto_fundamentals.ath_change_pct (CoinGecko-Cron). Negativer Wert =
   * unter ATH (z.B. -47.3 = 47.3% unter ATH). crypto.ts:73 nutzt Math.abs().
   * Vor W73 hardcoded null → ATH-Penalty in der Crypto-Strategie war permanent
   * tot. W73 fix: durchreichen aus SymbolDetail/SymbolScoreCell.
   */
  ath_change_pct?: number | null;
  /**
   * W94 — SPY-13W-Return für ECHTES Relative-Strength-Rating (Symbol-Return
   * minus Markt-Return). Quelle: MarketPhaseResult.spy_return_13w_pct (von
   * den Callern durchgereicht). Wenn null → Fallback auf die alte
   * pct-above-MA-Approximation.
   */
  spy_return_13w_pct?: number | null;
}

/**
 * Berechnet die `pct_above_ma40_weekly` aus den Wochen-Features.
 * Wenn keine Wochen-OHLCV verfügbar, fallback auf Daily-MA200.
 */
function pctAboveMa(price: number, ma: number | null): number | null {
  if (!ma || ma <= 0 || !price || price <= 0) return null;
  return ((price - ma) / ma) * 100;
}

export function buildStrategyFeatures(input: BuildFeaturesInput): StrategyFeatures | null {
  const { entry, quote, ohlcv_daily, ohlcv_weekly, ai, days_to_earnings } = input;
  if (!quote) return null;

  const themeTags = (entry.theme_tags ?? []) as ThemeTag[];
  const assetSubtype: AssetSubtype | null =
    (entry.asset_subtype as AssetSubtype | null) ??
    inferAssetSubtype({
      symbol: entry.symbol,
      asset_class: entry.asset_class as AssetClass,
      watchlist_role: entry.watchlist_role ?? null,
      theme_tags: themeTags,
    });

  // Bevorzugt Wochen-Features, sonst Daily-Fallback
  const ma40W = ohlcv_weekly?.ma_long ?? null;
  const ma200D = ohlcv_daily?.ma_long ?? null;
  const ma50D = ohlcv_daily?.ma_mid ?? null;

  const pctAboveMa40W =
    pctAboveMa(quote.current_price, ma40W) ?? pctAboveMa(quote.current_price, ma200D);

  const high52w = quote.high_52w ?? ai?.ai_high_52w_usd ?? 0;
  const low52w = quote.low_52w ?? 0;
  const drawdownFrom52wHigh =
    high52w > 0 && quote.current_price > 0
      ? ((quote.current_price - high52w) / high52w) * 100
      : null;
  const distanceTo52wHighPct =
    high52w > 0 && quote.current_price > 0
      ? ((high52w - quote.current_price) / high52w) * 100
      : null;

  // V5.1-Adapter: bestehende OhlcvFeatures haben keinen direkten consolidation_range_pct.
  // Approximation aus VCP-Daten (vcp_contractions_pct[]) — nimmt die kleinste Kontraktion als Range-Proxy.
  const consolidationRange = (() => {
    const arr = ohlcv_weekly?.vcp_contractions_pct ?? ohlcv_daily?.vcp_contractions_pct;
    if (!arr || arr.length === 0) return null;
    return Math.min(...arr);
  })();
  const consolidationDuration = ohlcv_weekly?.vcp_contractions_detected ?? null;

  // W94 — ECHTER MA200-Slope in %/Woche aus den Daily-Features (MA heute vs.
  // MA vor 5 Bars). Der alte Stufen-Proxy aus dem Wochen-Zähler hatte ein
  // Minimum von −0,1: "MA200 fällt"-KOs (longterm) waren unerreichbar und
  // die Antizyklik bekam ihr "Trend verflacht"-Plus (+8) für JEDEN Wert.
  // Weekly-Features liefern %/5-Wochen → bewusst NUR Daily nutzen.
  const maLongSlope = ohlcv_daily?.ma_long_slope_pct_per_week ?? null;

  return {
    symbol: entry.symbol,
    asset_class: entry.asset_class as AssetClass,
    asset_subtype: assetSubtype,
    theme_tags: themeTags,

    current_price: quote.current_price,
    high_52w: high52w,
    low_52w: low52w,
    drawdown_from_52w_high_pct: drawdownFrom52wHigh,

    ma200: ma200D,
    ma50: ma50D,
    ma40_weekly: ma40W,
    ma_long_slope_pct_per_week: maLongSlope,
    pct_above_ma40_weekly: pctAboveMa40W,
    // W44 (2026-05-17) — pct_above_ma200 fuer Universal-Extension-Penalty.
    pct_above_ma200:
      ma200D && ma200D > 0 ? ((quote.current_price - ma200D) / ma200D) * 100 : null,

    // Welle 13 — Überhitzungs-Bremse (overextension.ts) speisen.
    // EMA50-Distanz = pct_above_ma_mid (MA50). Wochen-RSI aus Weekly-Bars.
    // BB %B + Reife aus Daily-OhlcvFeatures. Reife in Wochen (÷5 Trading-Tage).
    pct_above_ema50: ohlcv_daily?.pct_above_ma_mid ?? null,
    rsi14_weekly: ohlcv_weekly?.rsi14 ?? null,
    bb_pct_b: ohlcv_daily?.bb_pct_b ?? null,
    weeks_since_pullback:
      ohlcv_daily?.bars_since_last_pullback != null
        ? Math.round(ohlcv_daily.bars_since_last_pullback / 5)
        : null,

    // W44 — Return-Felder aus Quote (Finnhub metric=all liefert sie)
    return_13w_pct: quote.return_13w_pct ?? null,
    return_26w_pct: quote.return_26w_pct ?? null,
    return_52w_pct: quote.return_52w_pct ?? null,

    // W94 — ECHTES Relative-Strength-Rating: Symbol-13W-Return minus
    // SPY-13W-Return, IBD-artig auf 0–99 gemappt (50 = marktgleich).
    // Der alte Proxy maß den Abstand zur EIGENEN MA200 — das ist absolute
    // Trendstärke, nicht relative: im Bull bekam fast alles "hohes RS".
    // Fallback auf die MA-Approximation, wenn SPY-Return (noch) fehlt.
    rs_rating: (() => {
      // W94/F15 — SYMMETRIE: Der SPY-Return stammt aus Schluss-Bars (65
      // Handelstage). Der Symbol-Return muss aus derselben Welt kommen,
      // sonst rauscht die Differenz ±6 Punkte (Finnhub-Quote = Kalender-
      // tage, teils intraday). Bars zuerst, Quote nur als Fallback.
      const symR = ohlcv_daily?.return_13w_pct_from_bars ?? quote.return_13w_pct ?? null;
      const spyR = input.spy_return_13w_pct ?? null;
      if (symR !== null && spyR !== null) {
        const d = symR - spyR; // Prozentpunkte über/unter dem Markt in 13W
        if (d <= -20) return 10;
        if (d <= 0) return Math.round(10 + (d + 20) * 2); // −20→10 … 0→50
        if (d <= 20) return Math.round(50 + d * 2); // 0→50 … +20→90
        // F15 — Sprungstelle geglättet (vorher 90 → 95 bei d=20,01)
        if (d <= 25) return Math.round(90 + (d - 20)); // +20→90 … +25→95
        return 95;
      }
      const pa = ohlcv_weekly?.pct_above_ma_long ?? ohlcv_daily?.pct_above_ma_long;
      if (pa === null || pa === undefined) return null;
      if (pa <= -10) return 20;
      if (pa <= 0) return 30 + (pa + 10) * 2;
      if (pa <= 10) return 50 + pa * 2.5;
      if (pa <= 30) return 75 + (pa - 10) * 0.75;
      return 90;
    })(),
    rs_distance_to_52w_high_pct: distanceTo52wHighPct,
    consolidation_range_pct: consolidationRange,
    consolidation_duration_weeks: consolidationDuration,

    ema50: ma50D,
    // W47 (2026-05-17) — RSI(14) aus OhlcvFeatures lesen. Vorher hardcoded
    // null -> Pullback-Strategie konnte RSI-Filter nicht anwenden.
    rsi14: ohlcv_daily?.rsi14 ?? null,
    swing_low_5: null,

    volume_avg_50d: ohlcv_daily?.avg_volume_50 ?? null,
    recent_volume_ratio: ohlcv_daily?.volume_ratio ?? null,
    volume_decline_during_pullback: null, // erfordert Pullback-Detection — späterer Step

    atr14: ohlcv_daily?.atr14 ?? null,
    // Wochenchart-Fokus: ATR aus Weekly-Bars (~14 Wochen) — passt zur
    // 4-26W-Hold-Dauer. Wenn keine Weekly-OHLCV verfügbar: null,
    // Stop/TP-Berechnung fällt automatisch auf Daily-ATR zurück.
    atr14_weekly: ohlcv_weekly?.atr14 ?? null,
    beta: quote.beta ?? null,

    days_to_next_earnings: days_to_earnings,
    last_earnings_surprise_pct: input.last_earnings_surprise_pct ?? null,
    last_earnings_day_reaction_pct: input.last_earnings_day_reaction_pct ?? null,

    roic_ttm_pct: quote.roic_ttm_pct ?? ai?.ai_roic_ttm_pct ?? null,
    fcf_margin_pct: quote.fcf_margin_pct ?? ai?.ai_fcf_margin_pct ?? null,
    operating_margin_pct: quote.operating_margin_pct ?? ai?.ai_operating_margin_pct ?? null,
    debt_to_equity: quote.debt_to_equity ?? ai?.ai_debt_to_equity ?? null,
    // Welle 11 — KGV für Bewertungs-Penalty in Pullback/Longterm
    pe_ttm: quote.pe_ttm ?? ai?.ai_pe_ttm ?? null,

    dividend_yield_pct: quote.dividend_yield_pct ?? ai?.ai_dividend_yield_pct ?? null,
    dividend_streak_years: ai?.dividend_years_increased ?? null,
    payout_ratio_pct: quote.payout_ratio_pct ?? ai?.ai_payout_ratio_pct ?? null,
    fcf_dividend_coverage: null,

    donchian_high_20w: ohlcv_weekly?.donchian_high_55 ?? null, // 55-Bar Wochen ≈ ATH-Approximation
    donchian_low_20w: null, // V5-OhlcvFeatures hat kein donchian_low — ATR-basierter Fallback in Krypto-Strategie
    btc_dominance_pct: input.btc_dominance_pct ?? null,
    // W73 Phase 6 — drawdown_from_ath_pct aktiviert: nutzt ath_change_pct aus
    // v5_crypto_fundamentals (CoinGecko-Cron). Vor W73 hardcoded null.
    drawdown_from_ath_pct: input.ath_change_pct ?? null,

    // W78 P1 — Audit-2 Module verkabelt
    short_interest_pct_of_float: input.short_interest_pct_of_float ?? null,
    historical_surprises_pct: input.historical_surprises_pct ?? null,

    // ===========================================================
    // Welle 14 — abgeleitete Felder aus v5_ai_research_cache
    // ===========================================================
    analyst_buy_ratio: (() => {
      // Finnhub-Free hat höchste Priorität (echte Quelle), AI als Fallback
      const sb = ai?.fh_analyst_strong_buy_count ?? null;
      const b = ai?.fh_analyst_buy_count ?? null;
      const h = ai?.fh_analyst_hold_count ?? null;
      const s = ai?.fh_analyst_sell_count ?? null;
      const ss = ai?.fh_analyst_strong_sell_count ?? null;
      const total =
        (sb ?? 0) + (b ?? 0) + (h ?? 0) + (s ?? 0) + (ss ?? 0);
      if (total >= 3) {
        return ((sb ?? 0) + (b ?? 0)) / total;
      }
      // Kein Finnhub-Konsens — AI-Sentiment-Anteile als ungefährer Ersatz
      if (ai?.ai_news_bullish_pct !== null && ai?.ai_news_bullish_pct !== undefined) {
        return Math.max(0, Math.min(1, (ai.ai_news_bullish_pct ?? 0) / 100));
      }
      return null;
    })(),
    analyst_upside_pct: (() => {
      const price = quote.current_price;
      if (!price || price <= 0) return null;
      // Bevorzugt Finnhub-Target, AI-Target als Fallback
      const tgt = ai?.fh_price_target_mean ?? ai?.ai_price_target_mean ?? null;
      if (tgt === null || tgt <= 0) return null;
      return ((tgt - price) / price) * 100;
    })(),
    news_sentiment_net: (() => {
      // Bevorzugt AI (zentralisierter Kontext via Perplexity), Finnhub als Fallback
      const bull = ai?.ai_news_bullish_pct ?? ai?.fh_news_sentiment_bullish_pct ?? null;
      const bear = ai?.ai_news_bearish_pct ?? ai?.fh_news_sentiment_bearish_pct ?? null;
      if (bull === null || bear === null) return null;
      return bull - bear;
    })(),
    news_count_7d: ai?.fh_news_count_7d ?? null,
  };
}

/**
 * V5.1 — Default Marktphasen-Result wenn echte SPY/VIX-Daten noch nicht da sind.
 * Liefert 'neutral' damit die Hauptscore-Auswahl funktional bleibt.
 */
export function defaultMarketPhase(): import('./market-phase').MarketPhaseResult {
  return {
    trend: 'range',
    volatility: 'normal',
    rotation: 'unknown',
    aggregate: 'neutral',
    summary: 'Marktphase noch nicht final berechnet (SPY/VIX-Daten fehlen) — neutral als Fallback.',
    preferred_strategies: ['pullback', 'relative_strength', 'longterm'],
    preferred_watchlist_roles: ['core', 'tactical'],
    hint_for_user: 'Selektives Trading. Quality-Pullbacks bevorzugt.',
    vix_level: null,
    spy_return_13w_pct: null,
  };
}

/**
 * V5.1 — Adapter: V5-MarketRegime → V5.1-MarketPhaseInput.
 *
 * Nutzt die vorhandenen V5-Regime-Flags als grobe Approximation, bis eine
 * echte SPY/VIX-Pipeline angebunden ist. Mappt:
 * - regime.stocks.is_supportive → spy_price>spy_ma200 + slope positiv
 * - regime.metals.is_supportive → gld_vs_spy_3m_pct >0
 * Phase wird dann über computeMarketPhase aus diesen Achsen abgeleitet.
 */
import type { MarketRegime } from '@/lib/scoring/types';
import type { MarketPhaseInput } from './market-phase';

interface RegimeBundle {
  stocks: MarketRegime | null;
  crypto: MarketRegime | null;
  bonds: MarketRegime | null;
  metals: MarketRegime | null;
}

export function regimeToMarketPhaseInput(regime: RegimeBundle): MarketPhaseInput {
  const stocksSupportive = regime.stocks?.is_supportive ?? false;
  const metalsSupportive = regime.metals?.is_supportive ?? false;

  const spyPrice = stocksSupportive ? 100 : 100;
  const spyMa200 = stocksSupportive ? 95 : 105;
  const spyMa50 = stocksSupportive ? 98 : 102;
  const slope = stocksSupportive ? 0.3 : -0.3;
  const adx = stocksSupportive ? 25 : 18;

  const stocksNotes = (regime.stocks?.notes ?? '').toLowerCase();
  const vixHigh = stocksNotes.includes('vola') || stocksNotes.includes('panic') || stocksNotes.includes('crash');
  const vix = vixHigh ? 28 : stocksSupportive ? 14 : 20;
  const vix20dHigh = vix * 1.1;

  return {
    spy_price: spyPrice,
    spy_ma200: spyMa200,
    spy_ma50: spyMa50,
    spy_ma200_slope_pct_per_week: slope,
    spy_adx14: adx,
    vix_level: vix,
    vix_20d_high: vix20dHigh,
    sector_rs: undefined,
    gld_vs_spy_3m_pct: metalsSupportive ? 12 : -2,
    ai_vs_spy_3m_pct: undefined,
  };
}

/**
 * Echte Daten statt Adapter — nutzt SPY-OHLCV-Features (inkl. ADX14),
 * VIX aus v5_vix_history und Sektor-RS aus SectorRotation.
 */
import type { SectorRotationSnapshot } from '@/lib/scoring/sector-rotation';
import type { VixSnapshot } from '@/hooks/use-market-regime';

export interface RealMarketPhaseInputArgs {
  spyPrice: number | null;
  spyFeatures: OhlcvFeatures | null;
  vix: VixSnapshot | null;
  sectorRotation: SectorRotationSnapshot | null;
  gldVsSpy3mPct?: number;
  aiVsSpy3mPct?: number;
}

export function buildRealMarketPhaseInput(args: RealMarketPhaseInputArgs): MarketPhaseInput {
  const { spyPrice, spyFeatures, vix, sectorRotation } = args;

  const sectorRs: Record<string, number> | undefined = sectorRotation
    ? Object.fromEntries(
        sectorRotation.rankings.map((s) => [s.symbol, s.rs_vs_spy_pct ?? 0])
      )
    : undefined;

  return {
    spy_price: spyPrice,
    spy_ma200: spyFeatures?.ma_long ?? null,
    spy_ma50: spyFeatures?.ma_mid ?? null,
    // W94 — echter %-Slope aus den Features (vorher Wochen-Zähler × 0,1
    // mit −0,1-Floor: "MA200 fällt" war für SPY nie erkennbar).
    spy_ma200_slope_pct_per_week: spyFeatures?.ma_long_slope_pct_per_week ?? null,
    spy_adx14: spyFeatures?.adx14 ?? null,
    vix_level: vix?.vix_close ?? null,
    vix_20d_high: vix?.vix_20d_high ?? null,
    // W94 — SPY-13W-Return für echtes RS-Rating
    spy_return_13w_pct: spyFeatures?.return_13w_pct_from_bars ?? null,
    sector_rs: sectorRs,
    gld_vs_spy_3m_pct: args.gldVsSpy3mPct,
    ai_vs_spy_3m_pct: args.aiVsSpy3mPct,
  };
}
